import mixpanel from "mixpanel-browser";

let isInitialized = false;
let lastPageView = { pathname: null, at: 0 };

// Simple in-memory engagement buffer keyed by pathname
const engagementBuffer = new Map(); // pathname -> { actions: Array<{name:string, meta:any, at:number}> }

function getBuffer(pathname) {
	const key = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
	if (!engagementBuffer.has(key)) engagementBuffer.set(key, { actions: [] });
	return engagementBuffer.get(key);
}

export function trackEngagementAction(actionName, meta = {}) {
	try {
		const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
		const buf = getBuffer(pathname);
		// Cap stored actions per page to avoid bloat
		if (buf.actions.length < 50) {
			buf.actions.push({ name: actionName, meta, at: Date.now() });
		}
		// Notify listeners that a new action arrived (for optional debounced flushes)
		if (typeof window !== 'undefined') {
			const evt = new CustomEvent('mp-engagement-action', { detail: { pathname, actionName } });
			window.dispatchEvent(evt);
		}
	} catch (err) {
		// Non-fatal
	}
}

export function consumeEngagementActions(pathname) {
	const key = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
	const buf = getBuffer(key);
	const out = buf.actions.slice();
	buf.actions.length = 0;
	return out;
}

export function initAnalytics(token) {
	if (isInitialized) return;
	try {
		if (!token) {
			console.warn("Mixpanel token missing; analytics disabled");
			return;
		}
		const apiHost = process.env.REACT_APP_MIXPANEL_API_HOST; // e.g., https://api-eu.mixpanel.com
		// Session Replay sampling percent (1 = 1%). Default to 5% if not provided.
		const recordSessionsPercentEnv = Number(process.env.REACT_APP_MIXPANEL_RECORD_SESSIONS_PERCENT);
		const recordSessionsPercent = Number.isFinite(recordSessionsPercentEnv) ? recordSessionsPercentEnv : 5;
		mixpanel.init(token, {
			debug: process.env.NODE_ENV !== "production",
			track_pageview: false,
			persistence: "localStorage",
			...(apiHost ? { api_host: apiHost } : {}),
			// Enable Session Replay sampling
			record_sessions_percent: recordSessionsPercent,
			// Enable Heatmaps (click map) by default; allow disabling via env
			record_heatmap_data: String(process.env.REACT_APP_MIXPANEL_RECORD_HEATMAP_DATA || 'true') !== 'false',
		});
		isInitialized = true;
	} catch (err) {
		console.error("Failed to initialize Mixpanel:", err);
	}
}

export function identifyUser(userId, peopleProps = {}) {
	try {
		if (!isInitialized || !userId) return;
		mixpanel.identify(userId);
		if (peopleProps && Object.keys(peopleProps).length > 0) {
			mixpanel.people.set(peopleProps);
		}
	} catch (err) {
		console.error("Mixpanel identify error:", err);
	}
}

export function setUserProperties(peopleProps = {}) {
	try {
		if (!isInitialized || !peopleProps || Object.keys(peopleProps).length === 0) return;
		mixpanel.people.set(peopleProps);
	} catch (err) {
		console.error("Mixpanel set user properties error:", err);
	}
}

export function trackEvent(eventName, properties = {}) {
	try {
		if (!isInitialized || !eventName) return;
		// Touch session and increment per-session counters for non-session events
		try {
			touchSessionActivity();
			if (eventName !== "Session Started" && eventName !== "Session Ended") {
				incrementSessionCounter('mp_events_count');
			}
		} catch (_) {}
		mixpanel.track(eventName, properties);
	} catch (err) {
		console.error("Mixpanel track error:", err);
	}
}

// Specialized helper for rationale-required applications
export function trackRationaleApplicationSubmitted(properties = {}) {
    try {
        // Standardize on a single event name for analytics
        trackEvent("Application Submitted - Rationale Role", {
            rationale_required: true,
            ...properties,
        });
    } catch (_) {}
}

export function trackPageView(pathname, properties = {}) {
	try {
		if (!isInitialized) return;
		const now = Date.now();
		// Deduplicate rapid repeat PVs (e.g., React StrictMode double effect in dev)
		if (lastPageView.pathname === pathname && now - lastPageView.at < 2000) {
			return;
		}
		lastPageView = { pathname, at: now };
		try {
			// Touch session, increment pages viewed
			touchSessionActivity();
			incrementSessionCounter('mp_pages_viewed');
		} catch (_) {}
		mixpanel.track("Page View", { pathname, ...properties });
	} catch (err) {
		console.error("Mixpanel page view error:", err);
	}
}

// ----------------------
// Session management
// ----------------------
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes

let sessionId = null;
let sessionStartMs = null;

function generateSessionId() {
	try {
		if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
	} catch (_) {}
	return `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function nowMs() { return Date.now(); }

function getLs(key, fallback = null) {
	try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch { return fallback; }
}

function setLs(key, value) {
	try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function removeLs(key) {
	try { localStorage.removeItem(key); } catch {}
}

function incrementSessionCounter(key) {
	const current = getLs(key, 0);
	setLs(key, (current || 0) + 1);
}

function startNewSession() {
	sessionId = generateSessionId();
	sessionStartMs = nowMs();
	setLs('mp_session_id', sessionId);
	setLs('mp_session_start_ms', sessionStartMs);
	setLs('mp_last_activity_ms', sessionStartMs);
	setLs('mp_events_count', 0);
	setLs('mp_pages_viewed', 0);
	setLs('mp_engaged_ms', 0);
	try { mixpanel.register({ session_id: sessionId }); } catch (_) {}
	try {
		mixpanel.track('Session Started', {
			session_id: sessionId,
			session_start_ts: new Date(sessionStartMs).toISOString(),
			pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
		});
	} catch (_) {}
}

function endCurrentSession(trigger = 'hidden') {
	const startMs = getLs('mp_session_start_ms', sessionStartMs);
	const sid = getLs('mp_session_id', sessionId);
	if (!startMs || !sid) return;
	const endMs = nowMs();
	const durationMs = Math.max(0, endMs - startMs);
	const engagedMs = getLs('mp_engaged_ms', 0) || 0;
	const pagesViewed = getLs('mp_pages_viewed', 0) || 0;
	const eventsCount = getLs('mp_events_count', 0) || 0;
	try {
		mixpanel.track('Session Ended', {
			session_id: sid,
			session_start_ts: new Date(startMs).toISOString(),
			session_end_ts: new Date(endMs).toISOString(),
			duration_s: Math.round(durationMs / 1000),
			engaged_seconds: Math.round((engagedMs || 0) / 1000),
			pages_viewed: pagesViewed,
			events_count: eventsCount,
			end_trigger: trigger,
			pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
		});
		try { mixpanel.people.set({ last_on_platform: new Date(endMs).toISOString(), last_session_duration_s: Math.round(durationMs / 1000) }); } catch (_) {}
	} catch (_) {}
	sessionId = null;
	sessionStartMs = null;
	// Clear persisted identifiers so next visibility will start a new session
	removeLs('mp_session_id');
	removeLs('mp_session_start_ms');
}

export function initSessionTracking() {
	try {
		const lastActivity = getLs('mp_last_activity_ms', 0) || 0;
		const start = getLs('mp_session_start_ms', 0) || 0;
		const existingId = getLs('mp_session_id', null);
		const gap = nowMs() - (lastActivity || 0);
		if (!existingId || !start || gap > INACTIVITY_LIMIT_MS) {
			startNewSession();
		} else {
			sessionId = existingId;
			sessionStartMs = start;
			try { mixpanel.register({ session_id: sessionId }); } catch (_) {}
		}

		const touch = () => touchSessionActivity();
		window.addEventListener('click', touch, { passive: true });
		window.addEventListener('mousemove', touch, { passive: true });
		window.addEventListener('keydown', touch, { passive: true });
		window.addEventListener('scroll', touch, { passive: true });
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') {
				// Give other hooks a chance to flush engagement first
				try { window.dispatchEvent(new Event('mp-session-will-end')); } catch (_) {}
				setTimeout(() => endCurrentSession('hidden'), 0);
			} else {
				// On return, always start a fresh session if the previous one ended (no id present)
				const existingId = getLs('mp_session_id', null);
				const last = getLs('mp_last_activity_ms', 0) || 0;
				if (!existingId) {
					startNewSession();
				} else if (nowMs() - last > INACTIVITY_LIMIT_MS) {
					startNewSession();
				} else {
					touchSessionActivity();
				}
			}
		});
		window.addEventListener('beforeunload', () => endCurrentSession('unload'));
	} catch (err) {
		// Non-fatal
	}
}

export function touchSessionActivity() {
	const last = getLs('mp_last_activity_ms', 0) || 0;
	const now = nowMs();
	if (now - last > INACTIVITY_LIMIT_MS) {
		endCurrentSession('timeout');
		startNewSession();
	}
	setLs('mp_last_activity_ms', now);
}

export function addSessionEngagedMs(deltaMs) {
	if (!deltaMs || deltaMs <= 0) return;
	const current = getLs('mp_engaged_ms', 0) || 0;
	setLs('mp_engaged_ms', current + deltaMs);
}

// ----------------------
// Session Replay helpers
// ----------------------
export function startSessionReplay() {
	try {
		if (!isInitialized || typeof mixpanel.start_session_recording !== 'function') return;
		mixpanel.start_session_recording();
	} catch (err) {
		// Non-fatal
	}
}

export function stopSessionReplay() {
	try {
		if (!isInitialized || typeof mixpanel.stop_session_recording !== 'function') return;
		mixpanel.stop_session_recording();
	} catch (err) {
		// Non-fatal
	}
}

export function getReplayProperties() {
	try {
		if (!isInitialized || typeof mixpanel.get_session_recording_properties !== 'function') return {};
		return mixpanel.get_session_recording_properties() || {};
	} catch (_) {
		return {};
	}
}

export function getReplayUrl() {
	try {
		if (!isInitialized || typeof mixpanel.get_session_replay_url !== 'function') return null;
		return mixpanel.get_session_replay_url() || null;
	} catch (_) {
		return null;
	}
}
